import React, { useState } from "react";
import Icons from "./Icons";
import { Card, btnPrimary, btnSecondary } from "./shared";
import { PLANS, redirectToCheckout, redirectToCustomerPortal, type PlanKey } from "../lib/stripe";

interface PricingPageProps {
  currentPlan?: PlanKey;
  userId?: string;
  isMobile: boolean;
  onClose?: () => void;
}

export default function PricingPage({ currentPlan = "free", userId, isMobile, onClose }: PricingPageProps) {
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleUpgrade(plan: "monthly" | "yearly") {
    if (!userId) {
      setError("Please sign in to upgrade.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await redirectToCheckout(plan, userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  async function handleManageBilling() {
    if (!userId) return;
    setLoading(true);
    try {
      await redirectToCustomerPortal(userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, fontFamily: "'DM Serif Display', serif", margin: 0 }}>
          Choose Your Plan
        </h1>
        {onClose && (
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 8 }}>
            <Icons.X style={{ width: 20, height: 20, color: "#6b7280" }} />
          </button>
        )}
      </div>
      <p style={{ color: "#6b7280", margin: "0 0 24px", fontSize: 14 }}>
        One plan for your whole household. Cancel anytime.
      </p>

      {/* Billing toggle */}
      <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 32, background: "#f3f4f6", borderRadius: 12, padding: 4, maxWidth: 280, margin: "0 auto 32px" }}>
        <button
          onClick={() => setBilling("monthly")}
          style={{
            flex: 1, padding: "8px 16px", borderRadius: 10, border: "none", fontSize: 13, fontWeight: 600,
            background: billing === "monthly" ? "#fff" : "transparent",
            color: billing === "monthly" ? "#1a1a1a" : "#6b7280",
            cursor: "pointer", fontFamily: "inherit",
            boxShadow: billing === "monthly" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
          }}
        >
          Monthly
        </button>
        <button
          onClick={() => setBilling("yearly")}
          style={{
            flex: 1, padding: "8px 16px", borderRadius: 10, border: "none", fontSize: 13, fontWeight: 600,
            background: billing === "yearly" ? "#fff" : "transparent",
            color: billing === "yearly" ? "#1a1a1a" : "#6b7280",
            cursor: "pointer", fontFamily: "inherit",
            boxShadow: billing === "yearly" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
          }}
        >
          Yearly
          <span style={{ fontSize: 10, color: "#16a34a", fontWeight: 700, marginLeft: 4 }}>Save 37%</span>
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 16, padding: "10px 16px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, color: "#dc2626", fontSize: 13, textAlign: "center" }}>
          {error}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 20, maxWidth: 900, margin: "0 auto" }}>
        {/* Free */}
        <Card style={{ padding: 28, border: currentPlan === "free" ? "2px solid #2d5a27" : "1px solid #eee" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Free</div>
          <div style={{ fontSize: 36, fontWeight: 700, fontFamily: "'DM Serif Display', serif", marginBottom: 4 }}>$0</div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>Forever</div>
          {currentPlan === "free" ? (
            <div style={{ ...btnSecondary, textAlign: "center", opacity: 0.6 } as React.CSSProperties}>Current Plan</div>
          ) : (
            <div style={{ ...btnSecondary, textAlign: "center", opacity: 0.6 } as React.CSSProperties}>Downgrade</div>
          )}
          <ul style={{ listStyle: "none", padding: 0, margin: "20px 0 0" }}>
            {PLANS.free.features.map((f, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151", padding: "6px 0" }}>
                <Icons.Check style={{ width: 16, height: 16, color: "#16a34a", flexShrink: 0 }} />
                {f}
              </li>
            ))}
          </ul>
        </Card>

        {/* Pro */}
        <Card style={{ padding: 28, border: "2px solid #2d5a27", position: "relative" }}>
          <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: "#2d5a27", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 14px", borderRadius: 20 }}>
            Most Popular
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#2d5a27", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Pro</div>
          <div style={{ fontSize: 36, fontWeight: 700, fontFamily: "'DM Serif Display', serif", marginBottom: 4 }}>
            ${billing === "yearly" ? "5.00" : "7.99"}
          </div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
            per month{billing === "yearly" ? ", billed annually" : ""}
          </div>
          {currentPlan === "monthly" || currentPlan === "yearly" ? (
            <button onClick={handleManageBilling} disabled={loading} style={{ ...btnPrimary, width: "100%", textAlign: "center", opacity: loading ? 0.6 : 1 } as React.CSSProperties}>
              {loading ? "Loading..." : "Manage Billing"}
            </button>
          ) : (
            <button onClick={() => handleUpgrade(billing)} disabled={loading} style={{ ...btnPrimary, width: "100%", textAlign: "center", opacity: loading ? 0.6 : 1 } as React.CSSProperties}>
              {loading ? "Loading..." : "Upgrade to Pro"}
            </button>
          )}
          <ul style={{ listStyle: "none", padding: 0, margin: "20px 0 0" }}>
            {PLANS.monthly.features.map((f, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151", padding: "6px 0" }}>
                <Icons.Check style={{ width: 16, height: 16, color: "#16a34a", flexShrink: 0 }} />
                {f}
              </li>
            ))}
          </ul>
        </Card>

        {/* Business */}
        <Card style={{ padding: 28, border: currentPlan === "business" ? "2px solid #2d5a27" : "1px solid #eee" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Business</div>
          <div style={{ fontSize: 36, fontWeight: 700, fontFamily: "'DM Serif Display', serif", marginBottom: 4 }}>$29.99</div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>per month</div>
          <button style={{ ...btnSecondary, width: "100%", textAlign: "center" } as React.CSSProperties}>
            Contact Sales
          </button>
          <ul style={{ listStyle: "none", padding: 0, margin: "20px 0 0" }}>
            {PLANS.business.features.map((f, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151", padding: "6px 0" }}>
                <Icons.Check style={{ width: 16, height: 16, color: "#16a34a", flexShrink: 0 }} />
                {f}
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* FAQ */}
      <div style={{ maxWidth: 600, margin: "48px auto 0" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, fontFamily: "'DM Serif Display', serif", textAlign: "center", marginBottom: 24 }}>
          Frequently Asked Questions
        </h2>
        {[
          { q: "Can I cancel anytime?", a: "Yes. Cancel from your billing portal and you'll keep Pro access until the end of your billing period." },
          { q: "What happens to my data if I downgrade?", a: "Your data is never deleted. You'll still see everything, but new additions will be limited to 50 items." },
          { q: "Is there a free trial?", a: "The Free plan is permanent and fully functional for small households. Upgrade only when you need more." },
          { q: "Do you offer refunds?", a: "Yes, we offer a 14-day money-back guarantee on all paid plans." },
        ].map(({ q, a }, i) => (
          <div key={i} style={{ marginBottom: 16, padding: "16px 20px", background: "#f9fafb", borderRadius: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>{q}</div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>{a}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
